import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AppButton } from '@/components/atoms';
import { Plus } from '@/components/atoms/AppIcon';
import { AdminClinicsListTemplate } from '@/components/templates';
import {
  InformationsFilterPanel,
  InformationsTableSection,
  AdminInformationPreviewModal,
} from '@/components/organisms';
import type { InformationPreviewData } from '@/components/organisms';
import type { AdminInformationItem } from '@/services/admin/informations';
import { useAdminInformationList } from '@/hooks/admin/useAdminInformationList';
import { useDeleteInformation } from '@/hooks/admin/useDeleteInformation';
import { useDownloadInformationsCsv } from '@/hooks/admin/useDownloadInformationsCsv';
import { ADMIN_ROUTES } from '@/constants/routes';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import type { InformationListParams } from '@/services/admin/informations';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';

const DEFAULT_PER_PAGE = 20;

type SortField = 'informations.id' | 'informations.information_date';
type SortDir = 'ascend' | 'descend';

type FilterState = {
  searchTitle: string;
  searchContents: string;
  availableDate: string;
  siteTypes: string[];
  serviceTypes: string[];
};

const getFiltersFromSearchParams = (sp: URLSearchParams): FilterState => ({
  searchTitle: sp.get('search_title') ?? '',
  searchContents: sp.get('search_contents') ?? '',
  availableDate: sp.get('available_date') ?? '',
  siteTypes: sp.get('search_site_type')?.split(',').filter(Boolean) ?? [],
  serviceTypes: sp.get('search_service_type')?.split(',').filter(Boolean) ?? [],
});

const getSortFromSearchParams = (
  sp: URLSearchParams
): { field: SortField; dir: SortDir } | null => {
  const sortBy = sp.get('sort_by');
  const order = sp.get('order');
  const validFields: string[] = ['informations.id', 'informations.information_date'];
  if (!sortBy || !validFields.includes(sortBy)) return null;
  if (order !== 'asc' && order !== 'desc') return null;
  return { field: sortBy as SortField, dir: order === 'asc' ? 'ascend' : 'descend' };
};

const getPageFromSearchParams = (sp: URLSearchParams): number => {
  const raw = Number(sp.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const SiteAdminInformationListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();
  const { downloadCsv } = useDownloadInformationsCsv();
 
  const [previewData, setPreviewData] = useState<InformationPreviewData | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
 
  const handlePreview = useCallback((row: AdminInformationItem) => {
    setPreviewData({
      title: row.title,
      information_date: row.information_date ?? '',
      contents: row.contents,
    });
    setIsPreviewOpen(true);
  }, []);

  // Disable Enter key navigation on this page (used for search inputs)
  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  // --- tenant site types ---
  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');
  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : site ?? ''],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !!site,
    staleTime: Infinity,
  });
  const tenantSiteTypeList: TenantSiteTypeItem[] =
    siteTypesQuery.data?.data?.site_types ?? [];
  // Spec: 表示対象 filter + column only for /super-admin/admin and /medix/admin
  const canUseSiteTypeFeatures = isSuperAdminRoute || site === 'medix';
  const showSiteTypeColumn = canUseSiteTypeFeatures;

  // --- filter state (input fields) ---
  const initialFilters = getFiltersFromSearchParams(searchParams);

  const [searchTitle, setSearchTitle] = useState(initialFilters.searchTitle);
  const [searchContents, setSearchContents] = useState(initialFilters.searchContents);
  const [availableDate, setAvailableDate] = useState(initialFilters.availableDate);
  const [siteTypes, setSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [serviceTypes, setServiceTypes] = useState<string[]>(initialFilters.serviceTypes);

  // --- applied filters (for query) ---
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(initialFilters);

  // --- sort & page ---
  const [sortState, setSortState] = useState<{ field: SortField; dir: SortDir } | null>(
    getSortFromSearchParams(searchParams)
  );
  const [page, setPage] = useState(getPageFromSearchParams(searchParams));

  // Sync from URL changes (browser back/forward)
  useEffect(() => {
    const f = getFiltersFromSearchParams(searchParams);
    setSearchTitle(f.searchTitle);
    setSearchContents(f.searchContents);
    setAvailableDate(f.availableDate);
    setSiteTypes(f.siteTypes);
    setServiceTypes(f.serviceTypes);
    setAppliedFilters(f);
    setPage(getPageFromSearchParams(searchParams));
    setSortState(getSortFromSearchParams(searchParams));
  }, [searchParams]);

  // --- build params for API ---
  const params: InformationListParams = useMemo(
    () => ({
      search_title: appliedFilters.searchTitle || undefined,
      search_contents: appliedFilters.searchContents || undefined,
      available_date: appliedFilters.availableDate || undefined,
      search_site_type:
        appliedFilters.siteTypes.length > 0 ? appliedFilters.siteTypes : undefined,
      search_service_type:
        appliedFilters.serviceTypes.length > 0 ? appliedFilters.serviceTypes : undefined,
      sort: sortState
        ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc']
        : undefined,
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [appliedFilters, sortState, page]
  );

  const { data, isLoading } = useAdminInformationList(site ?? null, params);

  // --- delete mutation ---
  const deleteMutation = useDeleteInformation();

  // Derived state for filter panels
  const tenantSiteTypeValues = tenantSiteTypeList
    .map(t => t.site_type)
    .filter(Boolean) as string[];
  // +1 for the 個別治療院 option
  const allSiteTypeOptionCount = tenantSiteTypeValues.length + 1;
  const hasAllSiteTypesSelected =
    siteTypes.length > 0 && siteTypes.length >= allSiteTypeOptionCount;
  const allServiceValues = ['A2', 'R2'];
  const hasAllServiceTypesSelected =
    serviceTypes.length > 0 && serviceTypes.length >= allServiceValues.length;

  // --- handlers ---
  const handleSearch = () => {
    if (!site) return;
    const next = new URLSearchParams();
    if (searchTitle.trim()) next.set('search_title', searchTitle.trim());
    if (searchContents.trim()) next.set('search_contents', searchContents.trim());
    if (availableDate.trim()) next.set('available_date', availableDate.trim());
    if (siteTypes.length > 0) next.set('search_site_type', siteTypes.join(','));
    if (serviceTypes.length > 0) next.set('search_service_type', serviceTypes.join(','));
    next.set('page', '1');
    // Spec item 8: search resets sort to default (created_at asc on backend)
    // Do NOT persist sort params → backend will use default sort

    flushSync(() => {
      setSearchParams(next);
      setPage(1);
      setSortState(null);
      setAppliedFilters({
        searchTitle,
        searchContents,
        availableDate,
        siteTypes,
        serviceTypes,
      });
    });
    void queryClient.refetchQueries({
      queryKey: ['admin', 'informations', site],
      type: 'active',
    });
  };

  const handleChangePage = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
  };

  const setSort = (field: SortField | null, dir: SortDir | null) => {
    const next = new URLSearchParams(searchParams);
    if (!field || !dir) {
      next.delete('sort_by');
      next.delete('order');
    } else {
      next.set('sort_by', field);
      next.set('order', dir === 'ascend' ? 'asc' : 'desc');
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleCsv = useCallback(() => {
    if (!site) return;
    const csvParams: Omit<InformationListParams, 'page' | 'per_page'> = {
      search_title: appliedFilters.searchTitle || undefined,
      search_contents: appliedFilters.searchContents || undefined,
      available_date: appliedFilters.availableDate || undefined,
      search_site_type: appliedFilters.siteTypes.length > 0 ? appliedFilters.siteTypes : undefined,
      search_service_type: appliedFilters.serviceTypes.length > 0 ? appliedFilters.serviceTypes : undefined,
      sort: sortState ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc'] : undefined,
    };
    downloadCsv(site, csvParams);
  }, [site, appliedFilters, sortState, downloadCsv]);

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const rows = data?.data ?? [];
  const meta = data?.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
  };

  if (!site) return null;

  return (
    <>
      <AdminClinicsListTemplate
      breadcrumbItems={[{ title: 'お知らせ' }]}
      title="お知らせ"
      filter={
        <InformationsFilterPanel
          canUseSiteTypeFeatures={canUseSiteTypeFeatures}
          tenants={tenantSiteTypeList}
          siteTypes={siteTypes}
          hasAllSiteTypesSelected={hasAllSiteTypesSelected}
          onChangeSiteTypes={setSiteTypes}
          searchTitle={searchTitle}
          onChangeSearchTitle={setSearchTitle}
          searchContents={searchContents}
          onChangeSearchContents={setSearchContents}
          availableDate={availableDate}
          onChangeAvailableDate={setAvailableDate}
          serviceTypes={serviceTypes}
          hasAllServiceTypesSelected={hasAllServiceTypesSelected}
          onChangeServiceTypes={setServiceTypes}
          onSearch={handleSearch}
          onCsv={handleCsv}
        />
      }
      betweenFilterAndTable={
        <div className="mb-4">
          <Link to={ADMIN_ROUTES.INFORMATIONS.CREATE(site)}>
            <AppButton icon={<Plus />} type="primary" className="py-1 my-1 btn-ra2-hover">
              新規
            </AppButton>
          </Link>
        </div>
      }
      table={
        <InformationsTableSection
          showSiteTypeColumn={showSiteTypeColumn}
          rows={rows}
          loading={isLoading}
          meta={meta}
          defaultPerPage={DEFAULT_PER_PAGE}
          onSort={(f, d) => setSort(f as SortField | null, d as SortDir | null)}
          onChangePage={handleChangePage}
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          onPreview={handlePreview}
        />
      }
    />
    <AdminInformationPreviewModal
      isOpen={isPreviewOpen}
      onClose={() => setIsPreviewOpen(false)}
      data={previewData}
    />
    </>
  );
};

export default SiteAdminInformationListPage;
